import { createRouter, createWebHistory } from "vue-router"
import { watch } from "vue"
import PublicLayout from "@/components/PublicLayout.vue"
import AppLayout from "@/components/AppLayout.vue"
import { i18n, translate } from "@/i18n"
import { useAuthStore } from "@/stores/auth"
import type { AccountRole } from "@/types/api"

declare module "vue-router" {
  interface RouteMeta {
    requiresAuth?: boolean
    publicOnly?: boolean
    minimumRole?: AccountRole
    title?: string
  }
}

const roleLevel: Record<AccountRole, number> = { CUSTOMER: 0, SUPPORT: 1, ADMIN: 2, SUPER_ADMIN: 3 }

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior: () => ({ top: 0 }),
  routes: [
    {
      path: "/",
      component: PublicLayout,
      children: [
        { path: "", name: "home", component: () => import("@/views/public/HomeView.vue"), meta: { title: "Prepaid AI API" } },
        { path: "pricing", name: "pricing", component: () => import("@/views/public/PricingView.vue"), meta: { title: "Pricing" } },
        { path: "models", name: "models", component: () => import("@/views/public/ModelsView.vue"), meta: { title: "Models" } },
        { path: "docs", name: "docs", component: () => import("@/views/public/DocsView.vue"), meta: { title: "API documentation" } },
        { path: "login", name: "login", component: () => import("@/views/auth/LoginView.vue"), meta: { title: "Sign in", publicOnly: true } },
        { path: "register", name: "register", component: () => import("@/views/auth/RegisterView.vue"), meta: { title: "Register", publicOnly: true } },
        { path: "verify-email", name: "verifyEmail", component: () => import("@/views/auth/VerifyEmailView.vue"), meta: { title: "Verify email" } },
        { path: "forgot-password", name: "forgotPassword", component: () => import("@/views/auth/ForgotPasswordView.vue"), meta: { title: "Forgot password", publicOnly: true } },
        { path: "reset-password", name: "resetPassword", component: () => import("@/views/auth/ResetPasswordView.vue"), meta: { title: "Reset password" } },
        { path: "mfa-enrollment", name: "mfaEnrollment", component: () => import("@/views/auth/MfaEnrollmentView.vue"),
          meta: { title: "Enroll MFA", requiresAuth: true } },
      ],
    },
    {
      path: "/dashboard",
      component: AppLayout,
      meta: { requiresAuth: true },
      children: [
        { path: "", name: "dashboard", component: () => import("@/views/customer/DashboardHomeView.vue"), meta: { title: "Dashboard" } },
        { path: "wallet", name: "wallet", component: () => import("@/views/customer/WalletView.vue"), meta: { title: "Wallet" } },
        { path: "payments", name: "payments", component: () => import("@/views/customer/PaymentsView.vue"), meta: { title: "Payments" } },
        { path: "api-keys", name: "apiKeys", component: () => import("@/views/customer/ApiKeysView.vue"), meta: { title: "API keys" } },
        { path: "usage", name: "usage", component: () => import("@/views/customer/UsageView.vue"), meta: { title: "Usage" } },
        { path: "sessions", name: "sessions", component: () => import("@/views/customer/SessionsView.vue"), meta: { title: "Sessions" } },
        { path: "security", name: "security", component: () => import("@/views/customer/SecurityView.vue"), meta: { title: "Security" } },
        { path: "settings", name: "settings", component: () => import("@/views/customer/SettingsView.vue"), meta: { title: "Settings" } },
        { path: "forbidden", name: "forbidden", component: () => import("@/views/PermissionDeniedView.vue"), meta: { title: "Permission denied" } },
      ],
    },
    {
      path: "/admin",
      component: AppLayout,
      meta: { requiresAuth: true, minimumRole: "SUPPORT" },
      children: [
        { path: "", name: "adminHome", component: () => import("@/views/admin/AdminHomeView.vue"), meta: { title: "Operator overview" } },
        { path: "accounts", name: "adminAccounts", component: () => import("@/views/admin/AdminAccountsView.vue"), meta: { title: "Accounts" } },
        { path: "accounts/:accountId", name: "adminAccount", component: () => import("@/views/admin/AdminAccountDetailView.vue"), meta: { title: "Account detail" } },
        { path: "payments", name: "adminPayments", component: () => import("@/views/admin/AdminPaymentsView.vue"), meta: { title: "Payments" } },
        { path: "payments/:paymentId", name: "adminPayment", component: () => import("@/views/admin/AdminPaymentDetailView.vue"), meta: { title: "Payment detail" } },
        { path: "usage", name: "adminUsage", component: () => import("@/views/admin/AdminUsageView.vue"), meta: { title: "Usage explorer" } },
        { path: "statistics", name: "adminStatistics", component: () => import("@/views/admin/AdminStatisticsView.vue"), meta: { title: "Statistics" } },
        { path: "models", name: "adminModels", component: () => import("@/views/admin/AdminModelsView.vue"), meta: { title: "Models", minimumRole: "ADMIN" } },
        { path: "payment-events", name: "adminPaymentEvents", component: () => import("@/views/admin/AdminPaymentEventsView.vue"), meta: { title: "Payment events" } },
        { path: "audit-log", name: "adminAudit", component: () => import("@/views/admin/AdminAuditView.vue"), meta: { title: "Audit log" } },
        { path: "jobs", name: "adminJobs", component: () => import("@/views/admin/AdminJobsView.vue"), meta: { title: "Jobs", minimumRole: "ADMIN" } },
        { path: "mail", name: "adminMail", component: () => import("@/views/admin/AdminMailView.vue"), meta: { title: "Mail", minimumRole: "ADMIN" } },
        { path: "system", name: "adminSystem", component: () => import("@/views/admin/AdminSystemView.vue"), meta: { title: "System health" } },
      ],
    },
    { path: "/:pathMatch(.*)*", name: "notFound", component: () => import("@/views/NotFoundView.vue"), meta: { title: "Not found" } },
  ],
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  if (to.meta.requiresAuth || to.meta.publicOnly) {
    try { await auth.loadSession() } catch { /* API error surfaces on the destination when appropriate. */ }
  }
  if (to.meta.publicOnly && auth.authenticated) return auth.enrollmentRequired ? { name: "mfaEnrollment" } : { name: "dashboard" }
  if (to.meta.requiresAuth && !auth.authenticated) return { name: "login", query: { redirect: to.fullPath } }
  if (auth.authenticated && auth.enrollmentRequired && to.name !== "mfaEnrollment") return { name: "mfaEnrollment" }
  if (to.name === "mfaEnrollment" && auth.authenticated && !auth.enrollmentRequired) return { name: "security" }
  if (to.meta.minimumRole && auth.user) {
    if (roleLevel[auth.user.role] < roleLevel[to.meta.minimumRole]) return { name: "forbidden" }
    if (!auth.mfaComplete) return auth.enrollmentRequired ? { name: "mfaEnrollment" } : { name: "forbidden" }
  }
  return true
})

function updateDocumentTitle(title: string | undefined): void {
  document.title = `${title ? translate(title) : "CodexNow"} · CodexNow`
}

router.afterEach((to) => updateDocumentTitle(to.meta.title))
watch(i18n.global.locale, () => updateDocumentTitle(router.currentRoute.value.meta.title))

export default router
